import React, { useState } from 'react';
import Sidebar from './Sidebar';

interface AIAssistantProps {
  onBackToDashboard: () => void;
  onNavigate: (view: 'dashboard' | 'quotations' | 'analytics' | 'settings' | 'ai-assistant' | 'user-management') => void;
}

const AIAssistant: React.FC<AIAssistantProps> = ({ onBackToDashboard, onNavigate }) => {

  const [messages, setMessages] = useState([
    {
      id: 1,
      type: 'ai',
      content: "Hello! I'm your AI Assistant. I can help you with quotation management, analytics, and insights. What would you like to know?",
      timestamp: new Date().toISOString()
    }
  ]);
  const [inputMessage, setInputMessage] = useState('');
  const [isTyping, setIsTyping] = useState(false);

  const handleSendMessage = async () => {
    if (!inputMessage.trim()) return;

    const userMessage = {
      id: messages.length + 1,
      type: 'user',
      content: inputMessage,
      timestamp: new Date().toISOString()
    };

    setMessages(prev => [...prev, userMessage]);
    setInputMessage('');
    setIsTyping(true);

    // Simulate AI response
    setTimeout(() => {
      const aiResponse = generateAIResponse(inputMessage);
      const aiMessage = {
        id: messages.length + 2,
        type: 'ai',
        content: aiResponse,
        timestamp: new Date().toISOString()
      };
      setMessages(prev => [...prev, aiMessage]);
      setIsTyping(false);
    }, 1500);
  };

  const generateAIResponse = (userInput: string): string => {
    const input = userInput.toLowerCase();
    
    if (input.includes('quotation') || input.includes('quote')) {
      return "I can help you with quotations! You have 12 pending quotations today. The average response time is 2.3 hours. Would you like me to show you the high-priority ones or help you draft a response?";
    }
    
    if (input.includes('analytics') || input.includes('performance')) {
      return "Your conversion rate is 23.5% this month, which is 3.2% higher than last month. Top performing channels are Email (28% conversion) and WhatsApp (22% conversion). Air freight quotations are showing the highest win rates.";
    }
    
    if (input.includes('customer') || input.includes('client')) {
      return "Your top customers this month are ABC Corp (15 quotations), XYZ Ltd (12 quotations), and DEF Industries (8 quotations). ABC Corp typically responds within 24 hours and has a 35% conversion rate.";
    }
    
    if (input.includes('price') || input.includes('pricing')) {
      return "Based on historical data, your optimal pricing strategy shows: Air freight quotations priced 8-12% below market average have 40% higher win rates. For ocean freight, maintain current pricing as it's competitive.";
    }
    
    if (input.includes('help') || input.includes('what can you do')) {
      return "I can help you with:\n• Quotation analysis and insights\n• Performance metrics and trends\n• Customer behavior patterns\n• Pricing recommendations\n• Response time optimization\n• Win/loss predictions\n\nJust ask me anything about your quotation management!";
    }
    
    if (input.includes('urgent') || input.includes('priority')) {
      return "You have 3 high-priority quotations requiring immediate attention:\n1. Q-2024-001 from ABC Corp (Air freight, $15K value)\n2. Q-2024-003 from XYZ Ltd (Ocean freight, $8K value)\n3. Q-2024-007 from DEF Industries (Express, $12K value)\n\nAll are approaching their SLA deadlines.";
    }
    
    return "I understand you're asking about: " + userInput + ". Let me analyze this for you. Based on your quotation data, I can provide insights and recommendations. Could you be more specific about what you'd like to know?";
  };

  const quickActions = [
    { label: "Show pending quotations", query: "Show me all pending quotations" },
    { label: "Performance summary", query: "Give me a performance summary" },
    { label: "Top customers", query: "Who are my top customers?" },
    { label: "Pricing insights", query: "What are the pricing insights?" },
    { label: "Urgent items", query: "Show me urgent quotations" }
  ];

  const handleQuickAction = (query: string) => {
    setInputMessage(query);
  };

  return (
    <div className="min-h-screen bg-gray-50 flex">
      {/* Sidebar */}
      <Sidebar currentView="ai-assistant" onNavigate={onNavigate} />

      {/* Main Content */}
      <div className="flex-1 ml-64">
        {/* Header */}
        <div className="bg-white shadow-sm border-b border-gray-200">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex justify-between items-center py-6">
              <div>
                <h1 className="text-3xl font-bold text-gray-900">AI Assistant</h1>
                <p className="text-gray-600 mt-1">Your intelligent quotation management copilot</p>
              </div>
              <div className="flex items-center space-x-4">
                <div className="flex items-center space-x-2 text-sm text-gray-600">
                  <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                  <span>AI Online</span>
                </div>
                <button className="btn-secondary">
                  <span className="mr-2">🔄</span>
                  Clear Chat
                </button>
              </div>
            </div>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
            {/* Chat Interface */}
            <div className="lg:col-span-3">
              <div className="card h-[600px] flex flex-col">
                {/* Messages */}
                <div className="flex-1 overflow-y-auto p-6 space-y-4">
                  {messages.map((message) => (
                    <div
                      key={message.id}
                      className={`flex ${message.type === 'user' ? 'justify-end' : 'justify-start'}`}
                    >
                      <div
                        className={`max-w-xs lg:max-w-md px-4 py-2 rounded-lg ${
                          message.type === 'user'
                            ? 'bg-primary-600 text-white'
                            : 'bg-gray-100 text-gray-900'
                        }`}
                      >
                        <p className="text-sm whitespace-pre-line">{message.content}</p>
                        <p className={`text-xs mt-1 ${
                          message.type === 'user' ? 'text-primary-100' : 'text-gray-500'
                        }`}>
                          {new Date(message.timestamp).toLocaleTimeString()}
                        </p>
                      </div>
                    </div>
                  ))}
                  
                  {isTyping && (
                    <div className="flex justify-start">
                      <div className="bg-gray-100 text-gray-900 px-4 py-2 rounded-lg">
                        <div className="flex items-center space-x-1">
                          <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"></div>
                          <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0.1s' }}></div>
                          <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                {/* Input */}
                <div className="border-t border-gray-200 p-4">
                  <div className="flex space-x-3">
                    <input
                      type="text"
                      value={inputMessage}
                      onChange={(e) => setInputMessage(e.target.value)}
                      onKeyPress={(e) => e.key === 'Enter' && handleSendMessage()}
                      placeholder="Ask me anything about your quotations..."
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                    />
                    <button
                      onClick={handleSendMessage}
                      disabled={!inputMessage.trim() || isTyping}
                      className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <span className="mr-2">📤</span>
                      Send
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {/* Quick Actions Sidebar */}
            <div className="lg:col-span-1">
              <div className="card">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Quick Actions</h3>
                <div className="space-y-3">
                  {quickActions.map((action, index) => (
                    <button
                      key={index}
                      onClick={() => handleQuickAction(action.query)}
                      className="w-full text-left p-3 bg-gray-50 hover:bg-gray-100 rounded-lg transition-colors"
                    >
                      <p className="text-sm font-medium text-gray-900">{action.label}</p>
                    </button>
                  ))}
                </div>
              </div>

              {/* AI Capabilities */}
              <div className="card mt-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">AI Capabilities</h3>
                <div className="space-y-3">
                  <div className="flex items-center space-x-3">
                    <span className="text-lg">📊</span>
                    <div>
                      <p className="text-sm font-medium text-gray-900">Analytics</p>
                      <p className="text-xs text-gray-600">Performance insights</p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-3">
                    <span className="text-lg">🎯</span>
                    <div>
                      <p className="text-sm font-medium text-gray-900">Predictions</p>
                      <p className="text-xs text-gray-600">Win/loss forecasting</p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-3">
                    <span className="text-lg">💰</span>
                    <div>
                      <p className="text-sm font-medium text-gray-900">Pricing</p>
                      <p className="text-xs text-gray-600">Optimal pricing suggestions</p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-3">
                    <span className="text-lg">👥</span>
                    <div>
                      <p className="text-sm font-medium text-gray-900">Customers</p>
                      <p className="text-xs text-gray-600">Behavior analysis</p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-3">
                    <span className="text-lg">⏰</span>
                    <div>
                      <p className="text-sm font-medium text-gray-900">Timing</p>
                      <p className="text-xs text-gray-600">Response optimization</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Recent Insights */}
              <div className="card mt-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Recent Insights</h3>
                <div className="space-y-3">
                  <div className="p-3 bg-green-50 rounded-lg">
                    <p className="text-sm font-medium text-green-900">Conversion Rate Up</p>
                    <p className="text-xs text-green-700">+3.2% this month</p>
                  </div>
                  <div className="p-3 bg-blue-50 rounded-lg">
                    <p className="text-sm font-medium text-blue-900">Response Time</p>
                    <p className="text-xs text-blue-700">2.3 hours average</p>
                  </div>
                  <div className="p-3 bg-purple-50 rounded-lg">
                    <p className="text-sm font-medium text-purple-900">Top Channel</p>
                    <p className="text-xs text-purple-700">Email (28% conversion)</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AIAssistant;
